import { useIntl } from 'react-intl'
import { isRewardFareClass, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import { REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'

interface Props {
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
  fareClass: FareClassWithReward
  /** When true, skip reward availability request and show hardcoded preview pricing instead. */
  rewardPreviewOnly?: boolean
}

/**
 * Renders the "from" pricing line for a fare class card in the Maldives charter modal.
 *
 * Cash variants show `From <amount> /adult` (single horizontal row).
 * Reward variants show `From` on its own line, then `<points> + <cash> /person` below.
 */
function MaldivesCharterFareClassModalBundleFromPrice({
  checkInDate,
  checkOutDate,
  occupancies,
  fareClass,
  rewardPreviewOnly = false,
}: Props) {
  const intl = useIntl()
  const isReward = isRewardFareClass(fareClass)

  const {
    adultPrice,
    fetching,
    rewardSeatPointsPerPerson,
    rewardSeatCashCoPayPerPerson,
    rewardAvailable,
  } = useCharterFlightPrice({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass,
    disabled: rewardPreviewOnly && isReward,
  })

  const fromLabel = intl.formatMessage({
    id: 'RUJ7Gofk5bJQT5Frz6g8',
    defaultMessage: 'From',
    description: 'Pricing label prefix before fare class price difference in charter flight modal',
  })
  const perAdultSuffix = intl.formatMessage({
    id: 'BUTmxzV-isuEf_HqftDr',
    defaultMessage: '/adult',
    description: 'Suffix after bundle “from” price in Maldives charter fare-class modal (per adult traveller)',
  })
  const perPersonSuffix = intl.formatMessage({
    id: 'ew2ARy7ro3tFSM4inpHN',
    defaultMessage: '/person',
    description: 'Per person pricing unit suffix in Maldives calendar',
  })
  const loadingLabel = intl.formatMessage({
    id: 'C6m8UbIIgveZbgrQtJ6T',
    defaultMessage: 'Loading...',
    description: 'Loading state text while charter flight prices are being fetched',
  })

  if (fetching) {
    return (
      <Caption variant="large" weight="normal" colour="neutral-two">
        {loadingLabel}
      </Caption>
    )
  }

  if (isReward) {
    const rate = REWARD_PREVIEW_RATE_BY_CABIN[toBaseFareClass(fareClass)]
    const usePreview = rewardPreviewOnly || !rewardAvailable || rewardSeatPointsPerPerson === undefined
    const displayPoints = usePreview ? rate?.pointsPerPerson : rewardSeatPointsPerPerson
    const displayCash = usePreview ? rate?.cashPerPerson : rewardSeatCashCoPayPerPerson
    if (displayPoints === undefined) {
      return null
    }
    return (
      <VerticalSpacer gap={4}>
        <PriceRowCaption>{fromLabel}</PriceRowCaption>
        <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
          <Caption variant="large" weight="bold" colour="neutral-one">
            <FormatLoyaltyPoints points={displayPoints} abbreviated />
            {displayCash !== undefined && displayCash > 0 && <>
              {' + '}
              <FormatCurrency value={displayCash} format="roundedDollar" />
            </>}
          </Caption>
          <Caption variant="medium" colour="neutral-three" weight="normal">
            {perPersonSuffix}
          </Caption>
        </Group>
      </VerticalSpacer>
    )
  }

  if (!adultPrice) {
    return null
  }

  return (
    <Group direction="horizontal" gap={4} verticalAlign="baseline">
      <PriceRowCaption>{fromLabel}</PriceRowCaption>
      <Caption variant="large" weight="bold" colour="neutral-one">
        <FormatCurrency value={adultPrice} format="roundedDollar" />
      </Caption>
      <Caption variant="medium" colour="neutral-three" weight="normal">
        {perAdultSuffix}
      </Caption>
    </Group>
  )
}

export default MaldivesCharterFareClassModalBundleFromPrice
